import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { Portfolio, Position, Transaction } from '../types';

export class PortfolioService {
  private static COLLECTION = 'portfolios';

  static async createPortfolio(portfolioData: Omit<Portfolio, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Portfolio> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const docRef = doc(collection(db, this.COLLECTION));
    const portfolio = {
      ...portfolioData,
      id: docRef.id,
      userId: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(docRef, portfolio);
    return { ...portfolio, createdAt: new Date(), updatedAt: new Date() } as Portfolio;
  }

  static async getPortfolio(portfolioId: string): Promise<Portfolio | null> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const docRef = doc(db, this.COLLECTION, portfolioId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      // Check if portfolio belongs to current user, has no userId (legacy data), or is shared
      if (!data.userId || data.userId === user.uid || data.isShared === true) {
        return {
          ...data,
          id: docSnap.id,
          userId: data.userId || user.uid, // For backward compatibility
          availableFunds: data.availableFunds || data.availableCash || data.initialFundValue || 0,
          totalEarnings: data.totalEarnings || 0,
          isShared: data.isShared || false,
          sharedAt: data.sharedAt?.toDate(),
          sharedSettings: data.sharedSettings,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Portfolio;
      } else {
        throw new Error('Access denied to this portfolio');
      }
    }
    
    return null;
  }

  static async getActivePortfolios(): Promise<Portfolio[]> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Get all active portfolios and filter client-side
    const q = query(
      collection(db, this.COLLECTION),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const portfolios: Portfolio[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include portfolios that belong to the current user or have no userId (legacy data)
      if (!data.userId || data.userId === user.uid) {
        portfolios.push({
          ...data,
          id: doc.id,
          userId: data.userId || user.uid,
          availableFunds: data.availableFunds || data.availableCash || data.initialFundValue || 0,
          totalEarnings: data.totalEarnings || 0,
          autopilotEnabled: data.autopilotEnabled || false,
          autopilotLastRun: data.autopilotLastRun?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Portfolio);
      }
    });
    
    return portfolios;
  }

  static async getAllPortfolios(): Promise<Portfolio[]> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Get all portfolios and filter client-side for backward compatibility
    const querySnapshot = await getDocs(collection(db, this.COLLECTION));
    const portfolios: Portfolio[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include portfolios that belong to the current user or have no userId (legacy data)
      if (!data.userId || data.userId === user.uid) {
        portfolios.push({
          ...data,
          id: doc.id,
          userId: data.userId || user.uid,
          availableFunds: data.availableFunds || data.availableCash || data.initialFundValue || 0,
          totalEarnings: data.totalEarnings || 0,
          autopilotEnabled: data.autopilotEnabled || false,
          autopilotLastRun: data.autopilotLastRun?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        } as Portfolio);
      }
    });
    
    return portfolios.sort((a, b) => {
      const dateA = a.createdAt || new Date(0);
      const dateB = b.createdAt || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }

  static async updatePortfolioValue(portfolioId: string, currentValue: number, availableFunds: number): Promise<void> {
    const docRef = doc(db, this.COLLECTION, portfolioId);
    await updateDoc(docRef, {
      currentValue,
      availableFunds,
      updatedAt: serverTimestamp()
    });
  }

  static async updateAvailableFunds(portfolioId: string, availableFunds: number): Promise<void> {
    const docRef = doc(db, this.COLLECTION, portfolioId);
    await updateDoc(docRef, {
      availableFunds,
      updatedAt: serverTimestamp()
    });
  }

  static async updatePortfolioAfterClose(
    portfolioId: string, 
    availableFunds: number,
    totalEarnings: number
  ): Promise<void> {
    const docRef = doc(db, this.COLLECTION, portfolioId);
    await updateDoc(docRef, {
      availableFunds,
      totalEarnings,
      updatedAt: serverTimestamp()
    });
  }

  static async getUserPortfolios(): Promise<Portfolio[]> {
    return this.getAllPortfolios();
  }

  static async deletePortfolio(portfolioId: string): Promise<void> {
    const docRef = doc(db, this.COLLECTION, portfolioId);
    await updateDoc(docRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    });
  }

  static calculatePortfolioMetrics(portfolio: Portfolio, positions: Position[]): {
    totalValue: number;
    totalGainLoss: number;
    totalGainLossPercent: number;
    positionsValue: number;
  } {
    // Only include open positions in portfolio value calculations
    const openPositions = positions.filter(p => p.status === 'open');
    
    // Current value of open positions
    const positionsValue = openPositions
      .reduce((sum, p) => sum + (p.currentPrice * p.quantity), 0);
    
    // Cost basis of open positions (what we paid)
    const positionsCost = openPositions
      .reduce((sum, p) => sum + (p.entryPrice * p.quantity), 0);
    
    // Performance is based only on executed positions
    const positionsGainLoss = positionsValue - positionsCost;
    const positionsGainLossPercent = positionsCost > 0 ? (positionsGainLoss / positionsCost) * 100 : 0;
    
    return {
      totalValue: positionsValue, // Only executed positions value
      totalGainLoss: positionsGainLoss,
      totalGainLossPercent: positionsGainLossPercent,
      positionsValue
    };
  }

  static async updatePortfolioName(portfolioId: string, newName: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, portfolioId);
      await updateDoc(docRef, {
        name: newName,
        updatedAt: serverTimestamp()
      });
      console.log(`Updated portfolio name to: ${newName}`);
    } catch (error) {
      console.error('Error updating portfolio name:', error);
      throw error;
    }
  }

  static async updatePortfolioCapacity(portfolioId: string, maxPositions: number): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, portfolioId);
      await updateDoc(docRef, {
        maxPositions,
        updatedAt: serverTimestamp()
      });
      console.log(`Updated portfolio capacity to: ${maxPositions} positions`);
    } catch (error) {
      console.error('Error updating portfolio capacity:', error);
      throw error;
    }
  }

  /**
   * Reset a virtual portfolio to its initial state
   * - Deletes all positions
   * - Deletes all trades/transactions
   * - Deletes all recommendations
   * - Resets availableFunds to initialFundValue
   * - Resets totalEarnings to 0
   * @param portfolioId The portfolio to reset
   * @throws Error if portfolio is not virtual type
   */
  /**
   * Link a virtual portfolio to Alpaca paper trading.
   * Only one portfolio can be linked at a time.
   */
  static async linkToAlpaca(portfolioId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Check no other portfolio is already linked
    const q = query(
      collection(db, this.COLLECTION),
      where('alpacaLinked', '==', true)
    );
    const existing = await getDocs(q);
    if (!existing.empty) {
      const linkedName = existing.docs[0].data().name || 'Another portfolio';
      throw new Error(`${linkedName} is already linked to Alpaca. Unlink it first.`);
    }

    const docRef = doc(db, this.COLLECTION, portfolioId);
    await updateDoc(docRef, {
      alpacaLinked: true,
      updatedAt: serverTimestamp()
    });
    console.log(`Portfolio ${portfolioId} linked to Alpaca`);
  }

  /**
   * Unlink a portfolio from Alpaca paper trading.
   */
  static async unlinkFromAlpaca(portfolioId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const docRef = doc(db, this.COLLECTION, portfolioId);
    await updateDoc(docRef, {
      alpacaLinked: false,
      updatedAt: serverTimestamp()
    });
    console.log(`Portfolio ${portfolioId} unlinked from Alpaca`);
  }

  static async resetPortfolio(portfolioId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Get the portfolio to verify it's virtual
    const portfolio = await this.getPortfolio(portfolioId);
    if (!portfolio) {
      throw new Error('Portfolio not found');
    }

    if (portfolio.type !== 'virtual') {
      throw new Error('Only virtual portfolios can be reset. Real portfolios contain actual trading history.');
    }

    console.log(`Resetting virtual portfolio: ${portfolio.name}`);

    // Use batch for atomic operations where possible
    const batch = writeBatch(db);

    // 1. Delete all positions for this portfolio
    const positionsQuery = query(
      collection(db, 'positions'),
      where('portfolioId', '==', portfolioId)
    );
    const positionsSnapshot = await getDocs(positionsQuery);
    console.log(`Deleting ${positionsSnapshot.size} positions`);
    positionsSnapshot.forEach((docSnap) => {
      batch.delete(docSnap.ref);
    });

    // 2. Delete all trades for this portfolio
    const tradesQuery = query(
      collection(db, 'trades'),
      where('portfolioId', '==', portfolioId)
    );
    const tradesSnapshot = await getDocs(tradesQuery);
    console.log(`Deleting ${tradesSnapshot.size} trades`);
    tradesSnapshot.forEach((docSnap) => {
      batch.delete(docSnap.ref);
    });

    // 3. Delete all recommendations for this portfolio
    const recommendationsQuery = query(
      collection(db, 'recommendations'),
      where('portfolioId', '==', portfolioId)
    );
    const recommendationsSnapshot = await getDocs(recommendationsQuery);
    console.log(`Deleting ${recommendationsSnapshot.size} recommendations`);
    recommendationsSnapshot.forEach((docSnap) => {
      batch.delete(docSnap.ref);
    });

    // 4. Reset portfolio values
    const portfolioRef = doc(db, this.COLLECTION, portfolioId);
    batch.update(portfolioRef, {
      availableFunds: portfolio.initialFundValue,
      totalEarnings: 0,
      currentValue: portfolio.initialFundValue,
      updatedAt: serverTimestamp()
    });

    // Commit all changes
    await batch.commit();

    console.log(`Portfolio "${portfolio.name}" reset successfully. ` +
      `Deleted: ${positionsSnapshot.size} positions, ${tradesSnapshot.size} trades, ` +
      `${recommendationsSnapshot.size} recommendations. ` +
      `Available funds reset to $${portfolio.initialFundValue}`);
  }
}